import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import UiIcon from '../../components/common/UiIcon';
import { approveKakaoPayPayment } from '../../lib/api';
import { trackEvent } from '../../lib/analytics';
import type { SpreadType } from '../../lib/types';

const PENDING_READING_KEY = 'ny_pending_payment_reading';
const KAKAOPAY_PENDING_KEY = 'ny_kakaopay_pending_checkout';

type PaymentProvider = 'kakaopay';

interface PendingReadingState {
  readingId: string;
  replayToken: string;
  spread?: SpreadType;
}

interface PendingKakaoPayState {
  orderType: 'wallet-recharge';
  packageId: string;
  readingId?: string;
  orderId: string;
  tid: string;
  amount: number;
  spread?: SpreadType;
}

const spreadLabels: Record<SpreadType, string> = {
  'one-card': '원카드 리딩',
  'three-card': '쓰리카드 리딩',
  'celtic-cross': '켈틱 크로스 리딩',
};

const statusCopy = {
  confirming: {
    title: '결제 상태를 확인하고 있습니다',
    description: '카카오페이 결제 승인 결과를 확인한 뒤 다음 화면으로 안전하게 이동합니다.',
  },
  generating: {
    title: '충전 결과를 반영하고 있습니다',
    description: '승인된 결제 정보를 반영해 포인트 상태를 업데이트하고 있습니다.',
  },
  success: {
    title: '정상적으로 처리되었습니다',
    description: '충전된 포인트를 반영한 뒤 다음 단계로 이동합니다.',
  },
  fail: {
    title: '결제 확인 중 문제가 발생했습니다',
    description: '카카오페이 승인 결과를 처리하는 중 오류가 발생했습니다.',
  },
} as const;

function readPendingState(readingId: string): PendingReadingState | null {
  if (typeof window === 'undefined') return null;

  const raw = window.sessionStorage.getItem(PENDING_READING_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as PendingReadingState;
    if (parsed.readingId === readingId && parsed.replayToken) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
}

function readKakaoPendingState(orderId: string, readingId?: string | null): PendingKakaoPayState | null {
  if (typeof window === 'undefined') return null;

  const raw = window.sessionStorage.getItem(KAKAOPAY_PENDING_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as PendingKakaoPayState;
    const readingMatches = readingId ? parsed.readingId === readingId : true;
    if (readingMatches && parsed.orderId === orderId && parsed.tid) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
}

function clearKakaoPendingState(orderId?: string, readingId?: string | null) {
  if (typeof window === 'undefined') return;

  const raw = window.sessionStorage.getItem(KAKAOPAY_PENDING_KEY);
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw) as PendingKakaoPayState;
    const orderMatches = !orderId || parsed.orderId === orderId;
    const readingMatches = readingId ? parsed.readingId === readingId : true;
    if (orderMatches && readingMatches) {
      window.sessionStorage.removeItem(KAKAOPAY_PENDING_KEY);
    }
  } catch {
    window.sessionStorage.removeItem(KAKAOPAY_PENDING_KEY);
  }
}

export const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasProcessedRef = useRef(false);
  const redirectTimerRef = useRef<number | null>(null);
  const [status, setStatus] = useState<'loading' | 'confirming' | 'generating' | 'success' | 'fail'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const provider = (searchParams.get('provider') as PaymentProvider | null) || 'kakaopay';
  const orderId = searchParams.get('orderId');
  const amount = searchParams.get('amount');
  const readingId = searchParams.get('readingId');
  const pgToken = searchParams.get('pg_token');
  const orderType = searchParams.get('orderType');
  const packageId = searchParams.get('packageId');
  const spread = (searchParams.get('spread') as SpreadType) || 'three-card';

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current !== null) {
        window.clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    const processKakaoRecharge = async () => {
      if (provider !== 'kakaopay') {
        setStatus('fail');
        setErrorMessage('현재 비활성화된 결제 경로입니다.');
        return;
      }

      if (!orderId || !amount || !pgToken) {
        setStatus('fail');
        setErrorMessage('카카오페이 승인 정보가 올바르지 않습니다.');
        return;
      }

      if (orderType !== 'wallet-recharge') {
        setStatus('fail');
        setErrorMessage('직접 결제 방식은 현재 비활성화되어 있습니다.');
        return;
      }

      const pendingKakao = readKakaoPendingState(orderId, readingId);
      if (!pendingKakao?.tid) {
        setStatus('fail');
        setErrorMessage('카카오페이 결제 대기 정보를 찾지 못했습니다. 충전 화면으로 돌아가 다시 시도해 주세요.');
        return;
      }

      const resolvedReadingId = readingId || pendingKakao.readingId;
      const resolvedSpread = pendingKakao.spread || spread;
      const pendingState = resolvedReadingId ? readPendingState(resolvedReadingId) : null;

      if (resolvedReadingId && !pendingState?.replayToken) {
        clearKakaoPendingState(orderId, resolvedReadingId);
        setStatus('fail');
        setErrorMessage('읽기 세션 정보가 만료되었습니다. 질문을 다시 선택해 주세요.');
        return;
      }

      try {
        setStatus('confirming');
        const approveResponse = await approveKakaoPayPayment({
          orderType: 'wallet-recharge',
          readingId: resolvedReadingId,
          packageId: packageId || pendingKakao.packageId,
          orderId,
          amount: Number(amount),
          pgToken,
          tid: pendingKakao.tid,
          spread: resolvedSpread,
        });

        if (!approveResponse.success) {
          setStatus('fail');
          setErrorMessage(approveResponse.message || '카카오페이 승인 처리에 실패했습니다.');
          return;
        }

        trackEvent('payment_success', {
          provider,
          order_type: orderType ?? '',
          amount_krw: Number(amount),
          package_id: packageId || pendingKakao.packageId,
          spread: resolvedSpread,
        });

        setStatus('generating');
        clearKakaoPendingState(orderId, resolvedReadingId);

        if (resolvedReadingId && pendingState?.replayToken) {
          window.sessionStorage.setItem(`reading_${resolvedReadingId}`, pendingState.replayToken);
        }

        setStatus('success');
        redirectTimerRef.current = window.setTimeout(() => {
          if (resolvedReadingId) {
            const nextParams = new URLSearchParams({
              readingId: resolvedReadingId,
              spread: resolvedSpread,
              recharged: '1',
              packageId: packageId || pendingKakao.packageId,
            });
            navigate(`/payment/checkout?${nextParams.toString()}`);
            return;
          }

          const nextParams = new URLSearchParams({
            recharged: '1',
            packageId: packageId || pendingKakao.packageId,
          });
          navigate(`/wallet/recharge?${nextParams.toString()}`);
        }, 800);
      } catch (error) {
        const message = error instanceof Error ? error.message : '카카오페이 승인 처리 중 오류가 발생했습니다.';
        setStatus('fail');
        setErrorMessage(message);
      }
    };

    void processKakaoRecharge();
  }, [amount, navigate, orderId, orderType, packageId, pgToken, provider, readingId, spread]);

  const activeState = status === 'loading' ? 'confirming' : status;
  const currentCopy = statusCopy[activeState];
  const spreadLabel = spreadLabels[spread] ?? spreadLabels['three-card'];

  return (
    <div className="min-h-screen bg-[#110d0c] px-4 py-8 text-white md:px-6 lg:px-8">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(251,191,36,0.12),transparent_32%),radial-gradient(circle_at_75%_20%,rgba(189,59,15,0.16),transparent_24%),linear-gradient(180deg,#110d0c_0%,#191210_45%,#110d0c_100%)]" />
      <div className="pointer-events-none fixed inset-0 bg-grain opacity-20 mix-blend-overlay" />

      <div className="relative z-10 mx-auto grid max-w-5xl gap-6 lg:grid-cols-[0.92fr_1.08fr]">
        <section className="border border-[#fbbf24]/15 bg-[#16110e]/82 p-6 shadow-[0_30px_80px_-35px_rgba(0,0,0,0.95)] backdrop-blur-sm md:p-7">
          <p className="text-[11px] uppercase tracking-[0.26em] text-[#fbbf24]/58">충전 확인</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-[0.02em] text-[#f8e7cf] md:text-4xl">
            결제 결과를 정리하고 있습니다
          </h1>
          <p className="mt-5 text-sm leading-7 text-[#ddc6a8]/78">
            충전이 끝나면 체크아웃으로 돌아가 {spreadLabel} 세션에서 바로 이어집니다.
          </p>

          <div className="mt-6 rounded-3xl border border-[#fbbf24]/15 bg-black/20 p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#fbbf24]/52">세션 정보</p>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-start justify-between gap-4">
                <dt className="text-[#bda78a]">스프레드</dt>
                <dd className="text-right text-[#f8ead6]">{spreadLabel}</dd>
              </div>
              <div className="flex items-start justify-between gap-4">
                <dt className="text-[#bda78a]">주문번호</dt>
                <dd className="max-w-[70%] break-all text-right font-medium text-[#f8ead6]">{orderId || '-'}</dd>
              </div>
              <div className="flex items-start justify-between gap-4">
                <dt className="text-[#bda78a]">충전금액</dt>
                <dd className="text-xl font-semibold text-[#fbbf24]">{Number(amount || 0).toLocaleString()}원</dd>
              </div>
              <div className="flex items-start justify-between gap-4">
                <dt className="text-[#bda78a]">결제수단</dt>
                <dd className="text-[#f8ead6]">카카오페이</dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="border border-[#fbbf24]/18 bg-[#f8f5f0] p-6 text-black shadow-[0_30px_80px_-35px_rgba(0,0,0,0.95)] md:p-7">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#8a6a4b]">진행 상태</p>
          <h2 className="mt-2 text-3xl font-semibold text-[#18110e]">{currentCopy.title}</h2>
          <p className="mt-4 text-sm leading-7 text-[#5b463b]">{currentCopy.description}</p>

          {(status === 'loading' || status === 'confirming' || status === 'generating') && (
            <div className="mt-8 flex flex-col items-center rounded-2xl border border-[#eadfce] bg-white px-6 py-10 text-center shadow-sm">
              <div className={`h-16 w-16 rounded-full border-4 ${status === 'generating' ? 'border-[#fbbf24]/30 border-t-[#fbbf24]' : 'border-[#2d8cff]/25 border-t-[#2d8cff]'} animate-spin`} />
              <p className="mt-6 text-base font-semibold text-[#18110e]">
                {status === 'generating' ? '충전 결과를 반영하고 있습니다' : '결제 승인 여부를 확인하고 있습니다'}
              </p>
              <p className="mt-3 max-w-sm text-sm leading-6 text-[#6a5448]">
                확인이 끝나면 체크아웃으로 돌아가 충전된 포인트로 현재 리딩을 이어갑니다.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="mt-8 rounded-2xl border border-[#dfe7d8] bg-[#f4faf0] px-6 py-10 text-center shadow-sm">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#c7f0b9]/50 text-[#2f8c27]">
                <UiIcon name="check" className="h-10 w-10" />
              </div>
              <p className="mt-6 text-xl font-semibold text-[#18331b]">곧 체크아웃으로 돌아갑니다</p>
              <p className="mt-3 text-sm leading-6 text-[#406146]">
                충전된 포인트를 현재 세션에 반영하는 중입니다.
              </p>
            </div>
          )}

          {status === 'fail' && (
            <div className="mt-8 rounded-2xl border border-[#f2d4d0] bg-[#fff6f5] px-6 py-8 shadow-sm">
              <div className="flex items-center gap-3 text-[#b34c43]">
                <UiIcon name="warning" className="h-8 w-8" />
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.16em]">충전 오류</p>
                  <p className="mt-1 text-base font-semibold">결제 정보를 끝까지 확인하지 못했습니다.</p>
                </div>
              </div>
              <p className="mt-4 text-sm leading-7 text-[#7a544f]">{errorMessage}</p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => navigate(`/payment/checkout?readingId=${encodeURIComponent(readingId || '')}&spread=${encodeURIComponent(spread)}`)}
                  className="inline-flex flex-1 items-center justify-center rounded-xl bg-[#18110e] px-5 py-4 text-sm font-semibold text-white transition-colors hover:bg-[#241811]"
                >
                  체크아웃으로 돌아가기
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/spreads')}
                  className="inline-flex flex-1 items-center justify-center rounded-xl border border-[#c9b8a7] bg-white px-5 py-4 text-sm font-semibold text-[#3c2e26] transition-colors hover:bg-[#faf4ee]"
                >
                  질문 다시 고르기
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
